// src/Redux/slices/doctorProfileSlice.js
import { createSlice } from "@reduxjs/toolkit";
import {
  pingDoctorProfiles,
  fetchDoctorInfoByDoctorId,
  createDoctorInfo,
  updateDoctorInfoByDoctorId,
  deleteDoctorInfoByDoctorId,
  upsertDoctorInfo,
  fetchDoctorInfoById,
  updateDoctorInfoById,
} from "../thunks/doctorProfileThunks";

const errMsg = (payload, error) => {
  if (typeof payload === "string") return payload;
  if (payload?.message) {
    if (Array.isArray(payload.message)) return payload.message.join(", ");
    return payload.message;
  }
  return error?.message || "Erreur";
};

const initialState = {
  ping: { ok: false, loading: false, error: null },
  doctorInfo: { data: null, loading: false, error: null, lastFetchedDoctorId: null },
  byId: { data: null, loading: false, error: null, lastFetchedId: null },
  mutation: { loading: false, error: null, lastAction: null },
};

const doctorProfileSlice = createSlice({
  name: "doctorProfile",
  initialState,
  reducers: {
    clearDoctorInfo(state) {
      state.doctorInfo.data = null;
      state.doctorInfo.error = null;
      state.doctorInfo.lastFetchedDoctorId = null;
    },
    clearDoctorInfoById(state) {
      state.byId.data = null;
      state.byId.error = null;
      state.byId.lastFetchedId = null;
    },
    clearDoctorProfileErrors(state) {
      state.ping.error = null;
      state.doctorInfo.error = null;
      state.byId.error = null;
      state.mutation.error = null;
    },

    // ✅ IMPORTANT: celui-là doit exister + être exporté
    resetDoctorProfile(state) {
      state.ping = { ok: false, loading: false, error: null };
      state.doctorInfo = { data: null, loading: false, error: null, lastFetchedDoctorId: null };
      state.byId = { data: null, loading: false, error: null, lastFetchedId: null };
      state.mutation = { loading: false, error: null, lastAction: null };
    },
  },

  extraReducers: (builder) => {
    /* PING */
    builder
      .addCase(pingDoctorProfiles.pending, (state) => {
        state.ping.loading = true;
        state.ping.error = null;
      })
      .addCase(pingDoctorProfiles.fulfilled, (state, action) => {
        state.ping.loading = false;
        state.ping.ok = !!action.payload?.ok;
      })
      .addCase(pingDoctorProfiles.rejected, (state, action) => {
        state.ping.loading = false;
        state.ping.error = errMsg(action.payload, action.error);
      });

    /* FETCH BY DOCTOR ID */
    builder
      .addCase(fetchDoctorInfoByDoctorId.pending, (state, action) => {
        state.doctorInfo.loading = true;
        state.doctorInfo.error = null;
        state.doctorInfo.lastFetchedDoctorId = action.meta.arg;
      })
      .addCase(fetchDoctorInfoByDoctorId.fulfilled, (state, action) => {
        state.doctorInfo.loading = false;
        state.doctorInfo.data = action.payload;
      })
      .addCase(fetchDoctorInfoByDoctorId.rejected, (state, action) => {
        state.doctorInfo.loading = false;
        state.doctorInfo.error = errMsg(action.payload, action.error);
      });

    /* FETCH BY ID */
    builder
      .addCase(fetchDoctorInfoById.pending, (state, action) => {
        state.byId.loading = true;
        state.byId.error = null;
        state.byId.lastFetchedId = action.meta.arg;
      })
      .addCase(fetchDoctorInfoById.fulfilled, (state, action) => {
        state.byId.loading = false;
        state.byId.data = action.payload;
      })
      .addCase(fetchDoctorInfoById.rejected, (state, action) => {
        state.byId.loading = false;
        state.byId.error = errMsg(action.payload, action.error);
      });

    const onMutPending = (state, lastAction) => {
      state.mutation.loading = true;
      state.mutation.error = null;
      state.mutation.lastAction = lastAction;
    };
    const onMutRejected = (state, action) => {
      state.mutation.loading = false;
      state.mutation.error = errMsg(action.payload, action.error);
    };
    const syncDoctorInfoIfSameDoctor = (state, updated) => {
      if (!updated?.doctorId) return;
      if (state.doctorInfo?.data?.doctorId === updated.doctorId) {
        state.doctorInfo.data = updated;
      }
    };

    /* CREATE */
    builder
      .addCase(createDoctorInfo.pending, (state) => onMutPending(state, "create"))
      .addCase(createDoctorInfo.fulfilled, (state, action) => {
        state.mutation.loading = false;
        state.doctorInfo.data = action.payload;
      })
      .addCase(createDoctorInfo.rejected, onMutRejected);

    /* UPSERT */
    builder
      .addCase(upsertDoctorInfo.pending, (state) => onMutPending(state, "upsert"))
      .addCase(upsertDoctorInfo.fulfilled, (state, action) => {
        state.mutation.loading = false;
        state.doctorInfo.data = action.payload;
      })
      .addCase(upsertDoctorInfo.rejected, onMutRejected);

    /* UPDATE BY DOCTOR ID */
    builder
      .addCase(updateDoctorInfoByDoctorId.pending, (state) => onMutPending(state, "updateDoctorId"))
      .addCase(updateDoctorInfoByDoctorId.fulfilled, (state, action) => {
        state.mutation.loading = false;
        if (!state.doctorInfo.data) state.doctorInfo.data = action.payload;
        syncDoctorInfoIfSameDoctor(state, action.payload);
      })
      .addCase(updateDoctorInfoByDoctorId.rejected, onMutRejected);

    /* UPDATE BY ID */
    builder
      .addCase(updateDoctorInfoById.pending, (state) => onMutPending(state, "updateId"))
      .addCase(updateDoctorInfoById.fulfilled, (state, action) => {
        state.mutation.loading = false;
        state.byId.data = action.payload;
        syncDoctorInfoIfSameDoctor(state, action.payload);
      })
      .addCase(updateDoctorInfoById.rejected, onMutRejected);

    /* DELETE BY DOCTOR ID */
    builder
      .addCase(deleteDoctorInfoByDoctorId.pending, (state) => onMutPending(state, "delete"))
      .addCase(deleteDoctorInfoByDoctorId.fulfilled, (state, action) => {
        state.mutation.loading = false;
        const deletedDoctorId = action.payload?.doctorId;
        if (state.doctorInfo.data?.doctorId === deletedDoctorId) {
          state.doctorInfo.data = null;
        }
      })
      .addCase(deleteDoctorInfoByDoctorId.rejected, onMutRejected);
  },
});

// ✅ IMPORTANT : exporter resetDoctorProfile ici
export const {
  clearDoctorInfo,
  clearDoctorInfoById,
  clearDoctorProfileErrors,
  resetDoctorProfile,
} = doctorProfileSlice.actions;

export default doctorProfileSlice.reducer;